import auth0 from "auth0-js";
import log from "loglevel";

import config from "@/config";

import { AUTH0_PASSWORDLESS_EMAIL_CONNECTION } from "../auth0Connections";

export type PasswordlessCaptchaWidget = {
  getValue: () => string | undefined;
  reload: (done?: () => void) => void;
};

export type PasswordlessCaptchaInit = PasswordlessCaptchaWidget & {
  required: boolean;
};

type WebAuthWithPasswordlessCaptcha = auth0.WebAuth & {
  renderPasswordlessCaptcha: (element: HTMLElement, options?: object | null, callback?: (error: unknown) => void) => PasswordlessCaptchaWidget;
};

let webAuthInstance: auth0.WebAuth | null = null;

/*
 * Email passwordless uses auth0-js WebAuth, not getAuth0Client(). @auth0/auth0-spa-js only supports
 * redirect-based Universal Login (loginWithRedirect); it has no passwordlessStart/passwordlessLogin.
 * auth0-js exposes embedded passwordless via cross-origin /co/authenticate and returns tokens on /end
 * as a URL hash, which we parse and bridge into session storage separately from the SPA SDK cache.
 */
function getWebAuth(): auth0.WebAuth {
  if (!webAuthInstance) {
    if (!config.auth0Domain || !config.auth0ClientId) {
      throw new Error("Auth0 is not configured (VITE_AUTH0_DOMAIN / VITE_AUTH0_CLIENT_ID)");
    }
    webAuthInstance = new auth0.WebAuth({
      domain: config.auth0Domain,
      clientID: config.auth0ClientId,
      redirectUri: `${window.location.origin}/end`,
      responseType: "token id_token",
      scope: "openid profile email",
    });
  }
  return webAuthInstance;
}

function isPasswordlessCaptchaRequired(element: HTMLElement): boolean {
  return element.style.display !== "none" && element.innerHTML.trim() !== "";
}

type PasswordlessStartWithCaptchaOptions = {
  connection: string;
  send: "code";
  email: string;
  captcha?: string;
};

export function promisifyPasswordlessStart(email: string, captcha?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const options: PasswordlessStartWithCaptchaOptions = {
      connection: AUTH0_PASSWORDLESS_EMAIL_CONNECTION,
      send: "code",
      email,
      captcha,
    };
    getWebAuth().passwordlessStart(options as auth0.PasswordlessStartOptions, (err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

function buildPasswordlessCaptchaInit(widget: PasswordlessCaptchaWidget, element: HTMLElement): PasswordlessCaptchaInit {
  return {
    getValue: widget.getValue,
    reload: widget.reload,
    required: isPasswordlessCaptchaRequired(element),
  };
}

export type PasswordlessCaptchaHandle = {
  widget: PasswordlessCaptchaWidget;
  whenReady: Promise<PasswordlessCaptchaInit>;
};

export function createPasswordlessCaptcha(element: HTMLElement): PasswordlessCaptchaHandle {
  let resolveReady!: (value: PasswordlessCaptchaInit) => void;
  let rejectReady!: (reason: unknown) => void;
  const whenReady = new Promise<PasswordlessCaptchaInit>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });

  const widget = (getWebAuth() as WebAuthWithPasswordlessCaptcha).renderPasswordlessCaptcha(element, null, (error) => {
    if (error) {
      rejectReady(error);
      return;
    }
    // auth0-js may invoke this callback synchronously before renderPasswordlessCaptcha returns.
    queueMicrotask(() => {
      resolveReady(buildPasswordlessCaptchaInit(widget, element));
    });
  });

  return { widget, whenReady };
}

export function waitForPasswordlessCaptchaReload(widget: PasswordlessCaptchaWidget, element: HTMLElement): Promise<PasswordlessCaptchaInit> {
  return new Promise((resolve, reject) => {
    widget.reload((error?: unknown) => {
      if (error) {
        reject(error);
        return;
      }
      resolve(buildPasswordlessCaptchaInit(widget, element));
    });
  });
}

export function promisifyPasswordlessLogin(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    getWebAuth().passwordlessLogin(
      {
        connection: AUTH0_PASSWORDLESS_EMAIL_CONNECTION,
        email,
        verificationCode: code,
      },
      (err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      }
    );
  });
}

export function promisifyParseHash(hash: string): Promise<auth0.Auth0DecodedHash | null> {
  return new Promise((resolve, reject) => {
    getWebAuth().parseHash({ hash }, (err, authResult) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(authResult);
    });
  });
}

export function promisifyWebAuthCheckSession(): Promise<auth0.Auth0DecodedHash | null> {
  return new Promise((resolve) => {
    getWebAuth().checkSession({}, (err, authResult) => {
      if (err || !authResult?.idToken) {
        if (err) {
          log.error("checkSession failed: ", err);
        }
        if (!authResult?.idToken) {
          log.error("checkSession failed: no idToken");
        }
        resolve(null);
        return;
      }
      resolve(authResult);
    });
  });
}
